import { supabase } from '../lib/supabase';
import { getProxyEndpoint, getProxyHeaders } from './proxy';

// Updated API base URLs
const ZONEDM_API_BASE = 'https://4d.zonedm.com';
const SERVICES_API_BASE = 'https://services.leadconnectorhq.com';

// Centralized proxy endpoint
const PROXY_ENDPOINT = getProxyEndpoint('higherlevel-proxy');

export type CustomValue = {
  id: string;
  name: string;
  fieldKey: string;
  value: string;
  locationId: string;
};

export type CustomValuesResponse = {
  customValues: CustomValue[];
};

export type ContactRequest = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dob?: string; // Format: YYYY-MM-DD
  address1?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  tags?: string[];
  customFields?: Record<string, string> | Array<{key?: string; id?: string; field_value: string}>;
  locationId?: string; // Required for upsert endpoint
};

export type ContactResponse = {
  contact: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    locationId: string;
    // ... other fields
  };
};

export type UpsertResponse = {
  IsNew: boolean;
  contact: {
    id: string;
    // ... other fields
  };
};

export class ZoneDMApi {
  private pit: string;
  private locationId: string;
  private userId?: string;

  constructor(credentials: { pit: string; locationId: string; userId?: string }) {
    this.pit = credentials.pit;
    this.locationId = credentials.locationId;
    this.userId = credentials.userId;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    baseUrl: string = ZONEDM_API_BASE
  ): Promise<T> {
    const isServices = baseUrl === SERVICES_API_BASE;
    const isCalendars = endpoint.startsWith('/calendars');
    const headers = {
      'Authorization': `Bearer ${this.pit}`,
      // Default modern version, override for calendars per docs
      'Version': isServices && isCalendars ? '2021-04-15' : '2021-07-28',
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };

    const url = `${baseUrl}${endpoint}`;
    console.log(`Making API request to: ${url}`);

    // Always use proxy for LeadConnector Services to avoid CORS and domain issues
    // (Don't attempt direct browser calls.)

    // Instead of direct API call, use our proxy function
    const proxyHeaders = getProxyHeaders();

    const response = await fetch(PROXY_ENDPOINT, {
      method: 'POST',
      headers: proxyHeaders,
      body: JSON.stringify({
        endpoint,
        method: options.method || 'GET',
        body: options.body ? JSON.parse(options.body as string) : undefined,
        headers,
        baseUrl, // Pass the base URL to the proxy
        // Lets higherlevel-proxy refresh+persist OAuth when access JWT is expired
        ...(this.userId ? { user_id: this.userId } : {}),
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorDetails = errorText;
      try {
        const errorJson = JSON.parse(errorText);
        errorDetails = JSON.stringify(errorJson, null, 2);
      } catch {
        // Keep as text if not JSON
      }
      console.error(`API Error: ${response.status} ${response.statusText}`);
      console.error(`Error details:`, errorDetails);
      console.error(`Request endpoint:`, endpoint);
      console.error(`Request method:`, options.method || 'GET');
      throw new Error(`ZoneDM API request failed (${response.status}): ${errorDetails}`);
    }

    return response.json();
  }

  // --- Custom Objects: Procedures ---
  private static readonly PROCEDURE_SCHEMA_KEY = 'custom_objects.procedure';
  private static readonly VERSION = '2021-07-28';

  async ensureProceduresObjectSchema(): Promise<void> {
    // Check or create schema
    try {
      await this.request(
        `/objects/${encodeURIComponent(ZoneDMApi.PROCEDURE_SCHEMA_KEY)}?locationId=${encodeURIComponent(this.locationId)}&fetchProperties=true`,
        {},
        SERVICES_API_BASE
      );
    } catch {
      await this.request(
        `/objects/`,
        {
          method: 'POST',
          body: JSON.stringify({
            labels: { singular: 'Procedure', plural: 'Procedures' },
            key: ZoneDMApi.PROCEDURE_SCHEMA_KEY,
            description: '4D EMR Procedures',
            locationId: this.locationId,
            primaryDisplayPropertyDetails: {
              key: 'custom_objects.procedure.name',
              name: 'Name',
              dataType: 'TEXT',
            },
          }),
        },
        SERVICES_API_BASE
      );
    }

    // Ensure searchable properties
    await this.request(
      `/objects/${encodeURIComponent(ZoneDMApi.PROCEDURE_SCHEMA_KEY)}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          locationId: this.locationId,
          searchableProperties: [
            'custom_objects.procedure.external_id',
            'custom_objects.procedure.name',
            'custom_objects.procedure.cpt',
          ],
          primaryDisplayPropertyDetails: {
            key: 'custom_objects.procedure.name',
            name: 'Name',
            dataType: 'TEXT',
          },
        }),
      },
      SERVICES_API_BASE
    );
  }

  private buildProcedureProperties(p: any) {
    const prefix = ZoneDMApi.PROCEDURE_SCHEMA_KEY + '.'; // 'custom_objects.procedure.'
    return {
      [prefix + 'external_id']: String(p.external_id),
      [prefix + 'name']: p.name,
      [prefix + 'cpt']: p.cpt || '',
      [prefix + 'is_active']: !!p.is_active,
      [prefix + 'procedure_fee']: p.procedure_fee ?? null,
      [prefix + 'duration_min']: p.duration_min ?? null,
      [prefix + 'facility_fee']: p.facility_fee ?? null,
      [prefix + 'anesthesia_fee']: p.anesthesia_fee ?? null,
      [prefix + 'modifier']: p.modifier || '',
      [prefix + 'or_procedure']: !!p.or_procedure,
      [prefix + 'office_service']: !!p.office_service,
      [prefix + 'note']: p.note || '',
    } as Record<string, any>;
  }

  async searchProcedureRecordByExternalId(externalId: string) {
    // Use filters-based search to match current API behavior
    const body = {
      locationId: this.locationId,
      page: 1,
      pageLimit: 5,
      filters: [
        {
          fieldKey: `${ZoneDMApi.PROCEDURE_SCHEMA_KEY}.external_id`,
          operator: 'EQ',
          values: [String(externalId)],
        },
      ],
      searchAfter: [],
    } as Record<string, any>;
    const res = await this.request<any>(
      `/objects/${encodeURIComponent(ZoneDMApi.PROCEDURE_SCHEMA_KEY)}/records/search`,
      { method: 'POST', body: JSON.stringify(body) },
      SERVICES_API_BASE
    );
    const records = res?.records || res?.data || [];
    return Array.isArray(records) && records.length ? records[0].id : null;
  }

  async upsertProcedureRecord(p: any) {
    const properties = this.buildProcedureProperties(p);
    const existingId = await this.searchProcedureRecordByExternalId(String(p.external_id));
    if (existingId) {
      // Update must include locationId and properties
      return this.request(
        `/objects/${encodeURIComponent(ZoneDMApi.PROCEDURE_SCHEMA_KEY)}/records/${encodeURIComponent(existingId)}`,
        { method: 'PUT', body: JSON.stringify({ locationId: this.locationId, properties }) },
        SERVICES_API_BASE
      );
    } else {
      // Create requires locationId in body, not query string
      return this.request(
        `/objects/${encodeURIComponent(ZoneDMApi.PROCEDURE_SCHEMA_KEY)}/records`,
        { method: 'POST', body: JSON.stringify({ locationId: this.locationId, properties }) },
        SERVICES_API_BASE
      );
    }
  }

  // --- Associations: contact ↔ procedure ---
  private static readonly CONTACT_PROCEDURE_ASSOC_KEY = 'contact_procedure';

  private async getAssociationByKey(key: string) {
    try {
      const res = await this.request<any>(
        `/associations/key/${encodeURIComponent(key)}?locationId=${encodeURIComponent(this.locationId)}`,
        {},
        SERVICES_API_BASE
      );
      return res;
    } catch {
      return null;
    }
  }

  async ensureContactProcedureAssociation(): Promise<string> {
    // Try fetch by key
    const existing = await this.getAssociationByKey(ZoneDMApi.CONTACT_PROCEDURE_ASSOC_KEY);
    if (existing?.id) return existing.id as string;

    // Create if missing
    const created = await this.request<any>(
      `/associations/`,
      {
        method: 'POST',
        body: JSON.stringify({
          locationId: this.locationId,
          key: ZoneDMApi.CONTACT_PROCEDURE_ASSOC_KEY,
          firstObjectLabel: 'Contact',
          firstObjectKey: 'contact',
          secondObjectLabel: 'Procedure',
          secondObjectKey: ZoneDMApi.PROCEDURE_SCHEMA_KEY,
        }),
      },
      SERVICES_API_BASE
    );
    return created?.id as string;
  }

  async createProcedureRelation(contactId: string, procedureRecordId: string): Promise<void> {
    const associationId = await this.ensureContactProcedureAssociation();
    await this.request(
      `/associations/relations`,
      {
        method: 'POST',
        body: JSON.stringify({
          locationId: this.locationId,
          associationId,
          firstRecordId: contactId,
          secondRecordId: procedureRecordId,
        }),
      },
      SERVICES_API_BASE
    );
  }

  async getCustomValues(): Promise<CustomValuesResponse> {
    // Correct GHL API endpoint (no /v2 prefix, camelCase customValues)
    return this.request<CustomValuesResponse>(
      `/locations/${this.locationId}/customValues`,
      {},
      SERVICES_API_BASE
    );
  }

  // --- Calendars ---
  async listCalendars(): Promise<any> {
    // Calendars endpoint requires trailing slash per docs
    return this.request<any>(
      `/calendars/?locationId=${encodeURIComponent(this.locationId)}`,
      {},
      SERVICES_API_BASE
    );
  }

  async listOpportunityCustomFields(): Promise<any> {
    return this.request<any>(
      `/locations/${this.locationId}/customFields?model=opportunity`,
      {},
      SERVICES_API_BASE
    );
  }

  // --- Pipelines ---
  async listPipelines(): Promise<any> {
    // Pipelines endpoint for opportunities
    return this.request<any>(
      `/opportunities/pipelines?locationId=${encodeURIComponent(this.locationId)}`,
      {},
      SERVICES_API_BASE
    );
  }

  async updateCustomValue(id: string, data: { name: string; value: string }): Promise<CustomValue> {
    // Correct GHL API endpoint (no /v2 prefix, camelCase customValues)
    return this.request<CustomValue>(
      `/locations/${this.locationId}/customValues/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      SERVICES_API_BASE
    );
  }

  async createCustomValue(data: { name: string; value: string }): Promise<CustomValue> {
    // Correct GHL API endpoint (no /v2 prefix, camelCase customValues)
    return this.request<CustomValue>(
      `/locations/${this.locationId}/customValues`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      SERVICES_API_BASE
    );
  }

  async sync4DCredentials(credentials: {
    clientId: string;
    clientSecret: string;
    subscriptionKey: string;
  }): Promise<void> {
    // Get existing custom values
    const { customValues } = await this.getCustomValues();

    // Define the custom values we want to sync
    const credentialsToSync = [
      { key: 'clientId', name: '4D Client ID', value: credentials.clientId },
      { key: 'clientSecret', name: '4D Client Secret', value: credentials.clientSecret },
      { key: 'subscriptionKey', name: '4D Subscription Key', value: credentials.subscriptionKey },
    ];

    // Sync each credential
    for (const cred of credentialsToSync) {
      const existingValue = customValues.find(cv => cv.name === cred.name);
      
      if (existingValue) {
        // Update existing custom value
        await this.updateCustomValue(existingValue.id, {
          name: cred.name,
          value: cred.value,
        });
      } else {
        // Create new custom value
        await this.createCustomValue({
          name: cred.name,
          value: cred.value,
        });
      }
    }
  }

  // Search for a contact by email or phone
  async searchContact(query: { email?: string; phone?: string }): Promise<ContactResponse | null> {
    try {
      // Build search params (location ID included in query params, not path)
      const searchParams = new URLSearchParams();
      searchParams.append('locationId', this.locationId);
      if (query.email) searchParams.append('email', query.email);
      if (query.phone) searchParams.append('phone', query.phone);

      // Correct GHL API endpoint
      const response = await this.request<{ contacts: any[] }>(
        `/contacts/search?${searchParams.toString()}`,
        {},
        SERVICES_API_BASE
      );

      if (response.contacts && response.contacts.length > 0) {
        return { contact: response.contacts[0] };
      }

      return null;
    } catch (error) {
      console.error('Error searching contact:', error);
      return null;
    }
  }

  // Create a new contact
  async createContact(contactData: ContactRequest): Promise<ContactResponse> {
    // Correct GHL API endpoint - include locationId in body
    return this.request<ContactResponse>(
      `/contacts/`,
      {
        method: 'POST',
        body: JSON.stringify({ ...contactData, locationId: this.locationId }),
      },
      SERVICES_API_BASE
    );
  }

  // Update an existing contact
  async updateContact(contactId: string, contactData: ContactRequest): Promise<ContactResponse> {
    // Correct GHL API endpoint
    return this.request<ContactResponse>(
      `/contacts/${contactId}`,
      {
        method: 'PUT',
        body: JSON.stringify(contactData),
      },
      SERVICES_API_BASE
    );
  }

  // Upsert a contact using the dedicated upsert endpoint
  async upsertContact(contactData: ContactRequest): Promise<{ contactId: string; IsNew: boolean }> {
    try {
      // Ensure locationId is included in the request
      const upsertData = {
        ...contactData,
        locationId: this.locationId,
      };
      
      // Convert customFields from Record to array format if needed
      if (upsertData.customFields && !Array.isArray(upsertData.customFields)) {
        upsertData.customFields = Object.entries(upsertData.customFields).map(([key, value]) => ({
          key,
          field_value: value
        }));
      }

      // Use the dedicated upsert endpoint
      const response = await this.request<UpsertResponse>(
        '/contacts/upsert',
        {
          method: 'POST',
          body: JSON.stringify(upsertData),
        },
        SERVICES_API_BASE // Use the services API base URL
      );

      return {
        contactId: response.contact.id,
        IsNew: response.IsNew
      };
    } catch (error) {
      console.error('Error upserting contact:', error);
      throw error;
    }
  }
}

// Create a singleton instance
let zonedmApiInstance: ZoneDMApi | null = null;

export function initializeZoneDMApi(credentials: {
  pit: string;
  locationId: string;
  userId?: string;
}): ZoneDMApi {
  zonedmApiInstance = new ZoneDMApi(credentials);
  return zonedmApiInstance;
}

export function getZoneDMApiInstance(): ZoneDMApi | null {
  return zonedmApiInstance;
}

// Convenience: push all procedure custom-object records for a user
export async function syncProceduresCustomObjectsForUser(userId: string) {
  if (!zonedmApiInstance) return;
  await zonedmApiInstance.ensureProceduresObjectSchema();
  const { data: procs } = await supabase
    .from('procedures')
    .select('*')
    .eq('user_id', userId);
  if (!Array.isArray(procs) || procs.length === 0) return;
  for (const p of procs) {
    try {
      await zonedmApiInstance.upsertProcedureRecord(p);
      await supabase.from('procedures').update({ sync_status: 'zonedm_synced' }).eq('id', p.id);
    } catch (e) {
      console.error('Procedure upsert failed', p.external_id, e);
      await supabase.from('procedures').update({ sync_status: 'zonedm_error' }).eq('id', p.id);
    }
  }
}